export interface Schedulable {
  durationMs: number
}

export interface Slot {
  /** Index into the array that was passed in. */
  index: number
  /** Wall-clock time the slot started (may be in the past). */
  startedAt: number
  /** Wall-clock time the slot ends. */
  endsAt: number
  /** How far into the item we currently are. */
  offsetMs: number
  cycleMs: number
}

export function cycleDurationMs(items: readonly Schedulable[]): number {
  let total = 0
  for (const item of items) total += item.durationMs
  return total
}

/**
 * Maps a wall-clock instant onto a position in the playlist.
 *
 * Deriving the current item from the clock instead of counting timer ticks means
 * every screen showing the same playlist stays in step without any coordination,
 * and a screen that was throttled, suspended or reloaded lands back on the item
 * its neighbours are showing. Requires the devices to keep a sane clock (NTP).
 *
 * Pass `epochMs = <session start>` to get plain sequential playback instead.
 */
export function resolveSlot(
  items: readonly Schedulable[],
  nowMs: number,
  epochMs = 0,
): Slot | null {
  const cycleMs = cycleDurationMs(items)
  if (items.length === 0 || cycleMs <= 0) return null

  let positionInCycle = (nowMs - epochMs) % cycleMs
  if (positionInCycle < 0) positionInCycle += cycleMs

  let elapsed = 0
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]
    if (!item) continue
    if (positionInCycle < elapsed + item.durationMs) {
      const offsetMs = positionInCycle - elapsed
      const startedAt = nowMs - offsetMs
      return { index, startedAt, endsAt: startedAt + item.durationMs, offsetMs, cycleMs }
    }
    elapsed += item.durationMs
  }

  // Only reachable through floating point drift; fall back to the last item.
  const index = items.length - 1
  const item = items[index]
  if (!item) return null
  return { index, startedAt: nowMs, endsAt: nowMs + item.durationMs, offsetMs: 0, cycleMs }
}

export function nextIndex(length: number, index: number): number {
  if (length <= 0) return 0
  return (index + 1) % length
}
